/**
 * useAppointments Hook
 * Custom hook for managing appointment list with filters, pagination and sorting
 *
 * @version 2025.1.0
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AppointmentResponse,
  AppointmentFilters,
  AppointmentListParams,
} from '../types/appointment';
import appointmentService from '../services/appointmentService';
import { useSnackbar } from 'notistack';

interface UseAppointmentsResult {
  appointments: AppointmentResponse[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  filters: AppointmentFilters;
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setFilters: (filters: AppointmentFilters) => void;
  setSorting: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void;
  refresh: () => void;
  cancelAppointment: (id: number) => Promise<boolean>;
  confirmAppointment: (id: number) => Promise<boolean>;
  completeAppointment: (id: number) => Promise<boolean>;
}

export const useAppointments = (initialParams?: AppointmentListParams): UseAppointmentsResult => {
  const { enqueueSnackbar } = useSnackbar();

  const [appointments, setAppointments] = useState<AppointmentResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(initialParams?.page ?? 1);
  const [limit, setLimit] = useState(initialParams?.limit ?? 20);
  const [filters, setFilters] = useState<AppointmentFilters>({
    companyId: initialParams?.companyId,
    employeeId: initialParams?.employeeId,
    clinicId: initialParams?.clinicId,
    doctorId: initialParams?.doctorId,
    status: initialParams?.status,
    examType: initialParams?.examType,
    startDate: initialParams?.startDate,
    endDate: initialParams?.endDate,
    search: initialParams?.search,
  });
  const [sortBy, setSortBy] = useState(initialParams?.sortBy || 'selectedDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(initialParams?.sortOrder || 'DESC');

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: AppointmentListParams = {
        page,
        limit,
        sortBy,
        sortOrder,
        ...filters,
      };

      console.log('[useAppointments] Fetching appointments with params:', params);
      const response = await appointmentService.getAppointments(params);
      console.log('[useAppointments] Response received:', response);

      setAppointments(response.appointments);
      setPagination({
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
      });
    } catch (err: unknown) {
      console.error('[useAppointments] Error fetching appointments:', err);
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao carregar agendamentos';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, filters, enqueueSnackbar]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleSetFilters = useCallback((newFilters: AppointmentFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleSetSorting = useCallback((newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  const refresh = useCallback(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const cancelAppointment = useCallback(async (id: number): Promise<boolean> => {
    try {
      await appointmentService.cancelAppointment(id);
      enqueueSnackbar('Agendamento cancelado com sucesso', { variant: 'success' });
      refresh();
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao cancelar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  const confirmAppointment = useCallback(async (id: number): Promise<boolean> => {
    try {
      await appointmentService.confirmAppointment(id);
      enqueueSnackbar('Agendamento confirmado com sucesso', { variant: 'success' });
      refresh();
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao confirmar agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  const completeAppointment = useCallback(async (id: number): Promise<boolean> => {
    try {
      await appointmentService.completeAppointment(id);
      enqueueSnackbar('Agendamento concluído com sucesso', { variant: 'success' });
      refresh();
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erro ao concluir agendamento';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, refresh]);

  return {
    appointments,
    loading,
    error,
    pagination,
    filters,
    sortBy,
    sortOrder,
    setPage: handleSetPage,
    setLimit: handleSetLimit,
    setFilters: handleSetFilters,
    setSorting: handleSetSorting,
    refresh,
    cancelAppointment,
    confirmAppointment,
    completeAppointment,
  };
};

export default useAppointments;
